import React, { useEffect, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import "./SalesPage.css";
import {
  getAuthToken,
  getCurrentShopId,
} from "./auth";

const API_URL = process.env.REACT_APP_API_URL || "https://api.deskpos.ie";

const getTodayDate = () => new Date().toISOString().slice(0, 10);

const parseItems = (sale) => {
  try {
    return typeof sale.items === "string"
      ? JSON.parse(sale.items || "[]")
      : sale.items || [];
  } catch {
    return [];
  }
};

const parsePaymentBreakdown = (sale) => {
  try {
    if (typeof sale.payment_breakdown === "string") {
      return JSON.parse(sale.payment_breakdown || "{}");
    }

    return sale.payment_breakdown || sale.paymentBreakdown || {};
  } catch {
    return {};
  }
};

const isRefundSale = (sale) => {
  if (!sale) return false;

  if (sale.is_refund === true) return true;
  if (sale.is_refund === "true") return true;

  const paymentMethod = String(
    sale.payment_method || sale.paymentMethod || ""
  ).toLowerCase();

  if (paymentMethod === "refund") return true;

  if (Number(sale.total || 0) < 0) return true;

  const items = parseItems(sale);

  return items.some((item) => item.is_refund || item.line_type === "refund");
};

const getProductNames = (sale) => {
  const items = parseItems(sale);

  if (!Array.isArray(items) || items.length === 0) return "-";

  return items
    .map((item) => {
      const name = String(item.name || "Refund Item").replace("REFUND - ", "");
      const qty = item.qty || 1;
      return `${name} x${qty}`;
    })
    .join(", ");
};

const getItemsCount = (sale) => {
  const items = parseItems(sale);

  return items.reduce((sum, item) => sum + Number(item.qty || 0), 0);
};

const getRefundPaymentTotals = (sale) => {
  const payment = parsePaymentBreakdown(sale);

  const cash = Number(
    payment.cash ??
      payment.cashPaid ??
      payment.cash_paid ??
      0
  );

  const card = Number(
    payment.card ??
      payment.cardPaid ??
      payment.card_paid ??
      0
  );

  return {
    cashRefund: Math.abs(Math.min(cash, 0)),
    cardRefund: Math.abs(Math.min(card, 0)),
  };
};

const RefundReportPage = () => {
  const today = getTodayDate();

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [refunds, setRefunds] = useState([]);
  const [summary, setSummary] = useState({
    refundTransactions: 0,
    refundTotal: 0,
    cashRefundTotal: 0,
    cardRefundTotal: 0,
    itemsReturned: 0,
  });
  const [shopId, setShopId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [tills, setTills] = useState([]);
  const [selectedTillId, setSelectedTillId] = useState("all");
  const [tillsLoading, setTillsLoading] = useState(false);

useEffect(() => {
  setShopId(getCurrentShopId());
  loadTills();

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

const loadTills = async () => {
  try {
    setTillsLoading(true);

    const token = getAuthToken();

    const response = await axios.get(`${API_URL}/tills`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const tillList = Array.isArray(response.data?.tills)
      ? response.data.tills
      : [];

    setTills(tillList);
  } catch (err) {
    console.error(
      "Load refund report tills error:",
      err.response?.data || err.message
    );

    setTills([]);
  } finally {
    setTillsLoading(false);
  }
};

  const calculateSummary = (refundSales) => {
    return refundSales.reduce(
      (acc, sale) => {
        const payment = getRefundPaymentTotals(sale);

        acc.refundTransactions += 1;
        acc.refundTotal += Math.abs(Number(sale.total || 0));
        acc.cashRefundTotal += payment.cashRefund;
        acc.cardRefundTotal += payment.cardRefund;
        acc.itemsReturned += getItemsCount(sale);

        return acc;
      },
      {
        refundTransactions: 0,
        refundTotal: 0,
        cashRefundTotal: 0,
        cardRefundTotal: 0,
        itemsReturned: 0,
      }
    );
  };

  const loadRefundReport = async () => {
    try {
      if (!shopId) {
        setError("Shop is still loading. Please wait.");
        return;
      }

      setLoading(true);
      setError("");

    const token = getAuthToken();

if (!token) {
  throw new Error(
    "Your login session has expired. Please log in again."
  );
}

const res = await axios.get(`${API_URL}/sales`, {
  params: {
    shop_id: shopId,
    from: fromDate,
    to: toDate,
    till_id:
      selectedTillId === "all"
        ? "all"
        : Number(selectedTillId),
  },
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

      const allSales = Array.isArray(res.data.sales) ? res.data.sales : [];

      const refundSales = allSales.filter(
        (sale) => !sale.is_void && isRefundSale(sale)
      );

      setRefunds(refundSales);
      setSummary(calculateSummary(refundSales));
    } catch (err) {
      console.error("Load refund report error:", err.response?.data || err.message);
      setError(err.response?.data?.error || "Failed to load refund report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shopId) {
      loadRefundReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);


  const getSelectedTillLabel = () => {
  if (selectedTillId === "all") {
    return "All Tills";
  }

  const selectedTill = tills.find(
    (till) =>
      String(till.id) === String(selectedTillId)
  );

  if (!selectedTill) {
    return "Selected Till";
  }

  return `${selectedTill.name} (${selectedTill.till_code})`;
};

  const exportRefundExcel = () => {
    const rows = [];

    rows.push(["REFUND REPORT"]);
    rows.push([]);
    rows.push(["Shop ID", shopId || "N/A"]);
    rows.push(["Till", getSelectedTillLabel()]);
    rows.push(["From", fromDate]);
    rows.push(["To", toDate]);
    rows.push([]);

    rows.push(["SUMMARY"]);
    rows.push(["Refund Transactions", summary.refundTransactions]);
    rows.push(["Refund Total", summary.refundTotal.toFixed(2)]);
    rows.push(["Cash Refunds", summary.cashRefundTotal.toFixed(2)]);
    rows.push(["Card Refunds", summary.cardRefundTotal.toFixed(2)]);
    rows.push(["Items Returned", summary.itemsReturned]);
    rows.push([]);

   rows.push([
     "Sale ID",
     "Date",
     "Till",
     "Till Code",
     "Cashier",
     "Cashier Email",
     "Products",
     "Items",
     "Refund Total",
     "Cash Refund",
     "Card Refund",
     "Payment Method",
   ]);

    refunds.forEach((sale) => {
      const payment = getRefundPaymentTotals(sale);

      rows.push([
        sale.id,
        sale.created_at ? new Date(sale.created_at).toLocaleString() : "-",
        sale.till_name || "Legacy / Unknown",
        sale.till_code || "",
        sale.user_name || "Unknown",
        sale.user_email || "",
        getProductNames(sale),
        getItemsCount(sale),
        Math.abs(Number(sale.total || 0)).toFixed(2),
        payment.cashRefund.toFixed(2),
        payment.cardRefund.toFixed(2),
        sale.payment_method || "refund",
      ]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(rows);

   worksheet["!cols"] = [
     { wch: 14 },
     { wch: 24 },
     { wch: 22 },
     { wch: 14 },
     { wch: 22 },
     { wch: 28 },
     { wch: 45 },
     { wch: 12 },
     { wch: 16 },
     { wch: 16 },
     { wch: 16 },
     { wch: 18 },
   ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Refund Report");

    const tillFileLabel =
      selectedTillId === "all" ? "all-tills" : `till-${selectedTillId}`;

    XLSX.writeFile(
      workbook,
      `refund-report-shop-${shopId}-${tillFileLabel}-${fromDate}-to-${toDate}.xlsx`,
    );
  };

  const exportRefundPdf = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    let y = 12;
    const margin = 10;
    const pageWidth = doc.internal.pageSize.getWidth();

    const money = (value) => `EUR ${Number(value || 0).toFixed(2)}`;

    const addPageIfNeeded = () => {
      if (y > 190) {
        doc.addPage();
        y = 12;
      }
    };

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Refund Report", margin, y);

    y += 8;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Shop ID: ${shopId || "N/A"}`, margin, y);
    doc.text(`Till: ${getSelectedTillLabel()}`, margin + 48, y);
    doc.text(`From: ${fromDate}   To: ${toDate}`, pageWidth - margin, y, {
      align: "right",
    });

    y += 10;

    doc.setFont("helvetica", "bold");
    doc.text(`Refund Transactions: ${summary.refundTransactions}`, margin, y);
    doc.text(`Refund Total: ${money(summary.refundTotal)}`, margin + 70, y);
    doc.text(`Cash Refunds: ${money(summary.cashRefundTotal)}`, margin + 140, y);
    doc.text(`Card Refunds: ${money(summary.cardRefundTotal)}`, margin + 210, y);

    y += 10;

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");

  const headers = [
    "Sale",
    "Date",
    "Till",
    "Cashier",
    "Products",
    "Qty",
    "Refund",
    "Cash",
    "Card",
    "Method",
  ];

  const widths = [16, 36, 25, 25, 64, 12, 23, 23, 23, 20];

    let x = margin;
    headers.forEach((h, i) => {
      doc.text(h, x, y);
      x += widths[i];
    });

    y += 5;
    doc.line(margin, y, pageWidth - margin, y);
    y += 5;

    doc.setFont("helvetica", "normal");

    refunds.forEach((sale) => {
      addPageIfNeeded();

      const payment = getRefundPaymentTotals(sale);

      const row = [
        `#${sale.id}`,
        sale.created_at
          ? new Date(sale.created_at).toLocaleString().substring(0, 18)
          : "-",
        String(sale.till_name || "Unknown").substring(0, 13),
        String(sale.user_name || "Unknown").substring(0, 13),
        getProductNames(sale).substring(0, 36),
        String(getItemsCount(sale)),
        money(Math.abs(Number(sale.total || 0))),
        money(payment.cashRefund),
        money(payment.cardRefund),
        sale.payment_method || "refund",
      ];

      let rowX = margin;

      row.forEach((cell, i) => {
        doc.text(String(cell), rowX, y);
        rowX += widths[i];
      });

      y += 6;
    });

    const tillFileLabel =
  selectedTillId === "all"
    ? "all-tills"
    : `till-${selectedTillId}`;

doc.save(
  `refund-report-shop-${shopId}-${tillFileLabel}-${fromDate}-to-${toDate}.pdf`,
);
  };

  return (
    <div className="sales-page">
      <div className="sales-header">
        <div>
          <h1 style={{ color: "#FFF" }}>Refund Report</h1>
          <p>View returned/refunded items between selected dates.</p>
        </div>

        <div className="sales-badge">{getSelectedTillLabel()}</div>
      </div>

      <div className="sales-filter-card">
        <div className="sales-filter">
          <label>
            From Date
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </label>

          <label>
            To Date
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </label>

          <label>
            Till
            <select
              value={selectedTillId}
              onChange={(e) => {
                setSelectedTillId(e.target.value);

                setRefunds([]);

                setSummary({
                  refundTransactions: 0,
                  refundTotal: 0,
                  cashRefundTotal: 0,
                  cardRefundTotal: 0,
                  itemsReturned: 0,
                });
              }}
              disabled={tillsLoading}
              className="sales-till-select"
            >
              <option value="all">All Tills</option>

              {tills.map((till) => (
                <option key={till.id} value={String(till.id)}>
                  {till.name} ({till.till_code})
                </option>
              ))}
            </select>
          </label>

          <button onClick={loadRefundReport} disabled={loading}>
            {loading ? "Loading..." : "Load Refunds"}
          </button>

          <button
            type="button"
            onClick={exportRefundExcel}
            disabled={loading || refunds.length === 0}
          >
            Export Excel
          </button>

          <button
            type="button"
            onClick={exportRefundPdf}
            disabled={loading || refunds.length === 0}
          >
            Export PDF
          </button>
        </div>
      </div>

      {error && <div className="sales-error">{error}</div>}

      <div className="sales-summary-grid">
        <div className="sales-summary-card">
          <span>Refund Transactions</span>
          <strong>{summary.refundTransactions}</strong>
        </div>

        <div className="sales-summary-card">
          <span>Refund Total</span>
          <strong>€{Number(summary.refundTotal || 0).toFixed(2)}</strong>
        </div>

        <div className="sales-summary-card">
          <span>Cash Refunds</span>
          <strong>€{Number(summary.cashRefundTotal || 0).toFixed(2)}</strong>
        </div>

        <div className="sales-summary-card">
          <span>Card Refunds</span>
          <strong>€{Number(summary.cardRefundTotal || 0).toFixed(2)}</strong>
        </div>

        <div className="sales-summary-card">
          <span>Items Returned</span>
          <strong>{summary.itemsReturned}</strong>
        </div>
      </div>

      <div className="sales-table-card">
        <h2>Refund List</h2>

        <div className="sales-table-wrap">
          <table className="sales-table">
            <thead>
              <tr>
                <th>Sale ID</th>
                <th>Date</th>
                <th>Till</th>
                <th>Cashier</th>
                <th>Products</th>
                <th>Items</th>
                <th>Refund Total</th>
                <th>Cash Refund</th>
                <th>Card Refund</th>
                <th>Payment</th>
              </tr>
            </thead>

            <tbody>
              {refunds.length === 0 ? (
                <tr>
                  <td colSpan="10" className="no-sales">
                    No refunds found for selected dates.
                  </td>
                </tr>
              ) : (
                refunds.map((sale) => {
                  const payment = getRefundPaymentTotals(sale);

                  return (
                    <tr key={sale.id}>
                      <td>#{sale.id}</td>
                      <td>
                        {sale.created_at
                          ? new Date(sale.created_at).toLocaleString()
                          : "-"}
                      </td>

                      <td>
                        <strong>{sale.till_name || "Legacy / Unknown"}</strong>

                        {sale.till_code && (
                          <div className="sales-cell-subtext">
                            {sale.till_code}
                          </div>
                        )}
                      </td>

                      <td>
                        <strong>{sale.user_name || "Unknown"}</strong>

                        {sale.user_email && (
                          <div className="sales-cell-subtext">
                            {sale.user_email}
                          </div>
                        )}
                      </td>
                      <td className="product-names-cell">
                        {getProductNames(sale)}
                      </td>
                      <td>{getItemsCount(sale)}</td>
                      <td>€{Math.abs(Number(sale.total || 0)).toFixed(2)}</td>
                      <td>€{Number(payment.cashRefund || 0).toFixed(2)}</td>
                      <td>€{Number(payment.cardRefund || 0).toFixed(2)}</td>
                      <td>{sale.payment_method || "refund"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RefundReportPage;